import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MapPin, Calendar, Clock, Users, Repeat, X } from 'lucide-react';
import { useBookingStore } from '../store/bookingStore';
import { publicApi } from '../lib/api';
import { toast } from 'sonner';
import { useLanguage } from '../locales/LanguageContext';

// Retorna "YYYY-MM-DD" en la zona horaria LOCAL del usuario.
// new Date().toISOString() usa UTC, lo que puede devolver la fecha de ayer
// en timezones negativos (GMT-1 a GMT-12, incluida toda América Latina).
const toLocalDateString = (date = new Date()) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const Hero = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const setRouteInfo = useBookingStore(state => state.setRouteInfo);
  const setQuoteData = useBookingStore(state => state.setQuoteData);
  const setCurrentStep = useBookingStore(state => state.setCurrentStep);
  const setPickupAddress = useBookingStore(state => state.setPickupAddress);
  const setPickupExactAddress = useBookingStore(state => state.setPickupExactAddress);
  const setDropoffExactAddress = useBookingStore(state => state.setDropoffExactAddress);
  
  const [routes, setRoutes] = useState([]);
  const [fromOptions, setFromOptions] = useState([]);
  const [toOptions, setToOptions] = useState([]);
  const [formData, setFormData] = useState({
    fromLabel: '',
    toLabel: '',
    pickupExactAddress: '',
    dropoffExactAddress: '',
    pickupAt: '',
    pickupTime: '12:00',
    passengers: 2,
    isRoundtrip: false,
    returnAt: '',
    returnTime: '12:00'
  });
  
  const [loading, setLoading] = useState(false);
  
  // Autocomplete states
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [filteredFromOptions, setFilteredFromOptions] = useState([]);
  const [filteredToOptions, setFilteredToOptions] = useState([]);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  // Load routes from API
  useEffect(() => {
    const loadRoutes = async () => {
      try {
        const response = await publicApi.getRoutes();
        setRoutes(response.data);
        
        // Extract unique origins
        const uniqueFrom = [...new Set(response.data.map(r => r.from_label))];
        setFromOptions(uniqueFrom);
        setFilteredFromOptions(uniqueFrom);
      } catch (error) {
        console.error('Error loading routes:', error);
        toast.error(t('hero.error_loading_routes'));
      }
    };
    
    loadRoutes();
  }, []);

  // Update destination options when origin changes
  useEffect(() => {
    if (formData.fromLabel) {
      const availableDestinations = routes
        .filter(r => r.from_label === formData.fromLabel)
        .map(r => r.to_label);
      setToOptions(availableDestinations);
      setFilteredToOptions(availableDestinations);
      
      // Reset destination if it's not valid for the new origin
      if (!availableDestinations.includes(formData.toLabel)) {
        setFormData(prev => ({ ...prev, toLabel: '' }));
        setToInput('');
      }
    } else {
      setToOptions([]);
      setFilteredToOptions([]);
      setFormData(prev => ({ ...prev, toLabel: '' }));
      setToInput('');
    }
  }, [formData.fromLabel, routes]);

  // Filter FROM options based on input
  useEffect(() => {
    if (fromInput === '') {
      setFilteredFromOptions(fromOptions);
    } else {
      const filtered = fromOptions.filter(option =>
        option.toLowerCase().includes(fromInput.toLowerCase())
      );
      setFilteredFromOptions(filtered);
    }
  }, [fromInput, fromOptions]);

  // Filter TO options based on input
  useEffect(() => {
    if (toInput === '') {
      setFilteredToOptions(toOptions);
    } else {
      const filtered = toOptions.filter(option =>
        option.toLowerCase().includes(toInput.toLowerCase())
      );
      setFilteredToOptions(filtered);
    }
  }, [toInput, toOptions]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target) &&
          fromInputRef.current && !fromInputRef.current.contains(event.target)) {
        setShowFromDropdown(false);
      }
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target) &&
          toInputRef.current && !toInputRef.current.contains(event.target)) {
        setShowToDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFromSelect = (option) => {
    setFormData({ ...formData, fromLabel: option });
    setFromInput(option);
    setShowFromDropdown(false);
  };

  const handleToSelect = (option) => {
    setFormData({ ...formData, toLabel: option });
    setToInput(option);
    setShowToDropdown(false);
  };

  const clearFrom = () => {
    setFormData({ ...formData, fromLabel: '' });
    setFromInput('');
    setShowFromDropdown(false);
  };

  const clearTo = () => {
    setFormData({ ...formData, toLabel: '' });
    setToInput('');
    setShowToDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fromLabel || !formData.toLabel || !formData.pickupAt) {
      toast.error(t('hero.error_required_fields'));
      return;
    }

    if (!formData.pickupExactAddress || formData.pickupExactAddress.trim().length < 5) {
      toast.error(t('hero.error_pickup_exact'));
      return;
    }

    if (!formData.dropoffExactAddress || formData.dropoffExactAddress.trim().length < 5) {
      toast.error(t('hero.error_dropoff_exact'));
      return;
    }

    const numPassengers = parseInt(formData.passengers);
    if (isNaN(numPassengers) || numPassengers < 1 || numPassengers > 50) {
      toast.error(t('hero.error_passengers'));
      return;
    }

    // Validate date range (today to 4 years from now)
    const today = toLocalDateString();
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 4);
    const max = toLocalDateString(maxDate);
    if (formData.pickupAt < today || formData.pickupAt > max) {
      toast.error(t('hero.error_date_range'));
      return;
    }

    if (formData.isRoundtrip && !formData.returnAt) {
      toast.error(t('hero.error_return_date'));
      return;
    }

    if (formData.isRoundtrip && formData.returnAt && (formData.returnAt < today || formData.returnAt > max)) {
      toast.error(t('hero.error_date_range'));
      return;
    }

    setLoading(true);
    
    try {
      const response = await publicApi.createQuote({
        from_label: formData.fromLabel,
        to_label: formData.toLabel,
        pickup_at: `${formData.pickupAt}T${formData.pickupTime}:00`,
        passengers: numPassengers,
        is_roundtrip: formData.isRoundtrip
      });
      
      // Save to store
      setRouteInfo(formData);
      setPickupExactAddress(formData.pickupExactAddress.trim());
      setDropoffExactAddress(formData.dropoffExactAddress.trim());
      setQuoteData(response.data.quote_id, response.data);
      setCurrentStep(1);
      
      // Navigate to wizard
      navigate('/booking');
      
      toast.success(t('hero.success_quote'));
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error(t('hero.error_creating_quote'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-navy-950">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=1920,h=1080,fit=crop/A3Qr2BKPp9ixwqK2/img_4804-YBgy7jxyWrhG3qPK.jpeg')`,
        }}
      />

      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(26, 26, 78, 0.75), rgba(30, 27, 91, 0.88))'
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl px-3 sm:px-4 py-8 md:py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
          {/* Left: Text */}
          <div className="text-white space-y-4 sm:space-y-6" data-testid="hero-text">
            <h1 className="font-manrope font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
              {t('hero.title')}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-indigo-200 leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4 pt-2 sm:pt-4">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-amber-400 rounded-full"></div>
                <span className="text-sm sm:text-base">{t('hero.professional_drivers')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-amber-400 rounded-full"></div>
                <span className="text-sm sm:text-base">{t('hero.fixed_price')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-amber-400 rounded-full"></div>
                <span className="text-sm sm:text-base">{t('hero.free_cancellation_24h')}</span>
              </div>
            </div>
          </div>
          
          {/* Right: Form */}
          <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 lg:p-8" data-testid="quote-form">
            <h2 className="font-manrope font-bold text-xl sm:text-2xl text-navy-950 mb-4 sm:mb-6">
              {t('hero.get_quote')}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* From */}
              <div className="relative">
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('hero.from')}
                </label>
                <div className="relative">
                  <input
                    ref={fromInputRef}
                    type="text"
                    value={fromInput}
                    onChange={(e) => setFromInput(e.target.value)}
                    onFocus={() => setShowFromDropdown(true)}
                    placeholder={t('hero.search_pickup')}
                    className="w-full h-11 sm:h-12 px-3 sm:px-4 pr-10 text-sm sm:text-base bg-white border border-slate-200 rounded-lg 
                      focus:border-navy-500 focus:ring-2 focus:ring-navy-200 
                      transition-all outline-none"
                    data-testid="from-input"
                    required={!formData.fromLabel}
                  />
                  {fromInput && (
                    <button
                      type="button"
                      onClick={clearFrom}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {/* Dropdown */}
                  {showFromDropdown && filteredFromOptions.length > 0 && (
                    <div
                      ref={fromDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {filteredFromOptions.map((option, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleFromSelect(option)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left text-sm sm:text-base hover:bg-indigo-50 transition-colors
                            first:rounded-t-lg last:rounded-b-lg border-b last:border-b-0 border-slate-100"
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700 flex-shrink-0" />
                            <span className="truncate">{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showFromDropdown && filteredFromOptions.length === 0 && fromInput && (
                    <div
                      ref={fromDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 sm:p-4"
                    >
                      <p className="text-sm text-slate-500 text-center">{t('hero.no_locations_found')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pickup Exact Address - visible after origin selection */}
              {formData.fromLabel && (
                <div className="transition-all duration-300 pl-2 border-l-2 border-navy-400">
                  <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                    {t('hero.pickup_exact_label')} *
                  </label>
                  <input
                    type="text"
                    value={formData.pickupExactAddress}
                    onChange={(e) => setFormData({...formData, pickupExactAddress: e.target.value})}
                    placeholder={t('hero.pickup_exact_placeholder')}
                    className="w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border border-slate-200 rounded-lg
                      focus:border-navy-500 focus:ring-2 focus:ring-navy-200
                      transition-all outline-none"
                    data-testid="pickup-exact-address-input"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">{t('hero.pickup_exact_hint')}</p>
                </div>
              )}

              {/* To */}
              <div className="relative">
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('hero.to')}
                </label>
                <div className="relative">
                  <input
                    ref={toInputRef}
                    type="text"
                    value={toInput}
                    onChange={(e) => setToInput(e.target.value)}
                    onFocus={() => setShowToDropdown(true)}
                    placeholder={formData.fromLabel ? t('hero.search_destination') : t('hero.select_origin_first')}
                    disabled={!formData.fromLabel}
                    className="w-full h-11 sm:h-12 px-3 sm:px-4 pr-10 text-sm sm:text-base bg-white border border-slate-200 rounded-lg 
                      focus:border-navy-500 focus:ring-2 focus:ring-navy-200 
                      transition-all outline-none disabled:bg-slate-50 disabled:cursor-not-allowed"
                    data-testid="to-input"
                    required={!formData.toLabel}
                  />
                  {toInput && formData.fromLabel && (
                    <button
                      type="button"
                      onClick={clearTo}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {/* Dropdown */}
                  {showToDropdown && filteredToOptions.length > 0 && formData.fromLabel && (
                    <div
                      ref={toDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {filteredToOptions.map((option, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleToSelect(option)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left text-sm sm:text-base hover:bg-indigo-50 transition-colors
                            first:rounded-t-lg last:rounded-b-lg border-b last:border-b-0 border-slate-100"
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700 flex-shrink-0" />
                            <span className="truncate">{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showToDropdown && filteredToOptions.length === 0 && toInput && formData.fromLabel && (
                    <div
                      ref={toDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 sm:p-4"
                    >
                      <p className="text-sm text-slate-500 text-center">{t('hero.no_locations_found')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Dropoff Exact Address - visible after destination selection */}
              {formData.toLabel && (
                <div className="transition-all duration-300 pl-2 border-l-2 border-navy-400">
                  <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                    {t('hero.dropoff_exact_label')} *
                  </label>
                  <input
                    type="text"
                    value={formData.dropoffExactAddress}
                    onChange={(e) => setFormData({...formData, dropoffExactAddress: e.target.value})}
                    placeholder={t('hero.dropoff_exact_placeholder')}
                    className="w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border border-slate-200 rounded-lg
                      focus:border-navy-500 focus:ring-2 focus:ring-navy-200
                      transition-all outline-none"
                    data-testid="dropoff-exact-address-input"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">{t('hero.dropoff_exact_hint')}</p>
                </div>
              )}

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1 sm:gap-2">
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                    <span className="truncate">{t('hero.date')}</span>
                  </label>
                  <input
                    type="date"
                    value={formData.pickupAt}
                    onChange={(e) => setFormData({...formData, pickupAt: e.target.value})}
                    min={toLocalDateString()}
                    max={toLocalDateString(new Date(new Date().setFullYear(new Date().getFullYear() + 4)))}
                    className="w-full h-11 sm:h-12 px-2 sm:px-4 text-sm sm:text-base bg-white border border-slate-200 rounded-lg
                      focus:border-navy-500 focus:ring-2 focus:ring-navy-200
                      transition-all outline-none"
                    data-testid="date-input"
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1 sm:gap-2">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                    <span className="truncate">{t('hero.time')}</span>
                  </label>
                  <input
                    type="time"
                    value={formData.pickupTime}
                    onChange={(e) => setFormData({...formData, pickupTime: e.target.value})}
                    className="w-full h-11 sm:h-12 px-2 sm:px-4 text-sm sm:text-base bg-white border border-slate-200 rounded-lg 
                      focus:border-navy-500 focus:ring-2 focus:ring-navy-200 
                      transition-all outline-none"
                    data-testid="time-input"
                  />
                </div>
              </div>
              
              {/* Passengers */}
              <div>
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('hero.passengers')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={formData.passengers}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setFormData({...formData, passengers: ''});
                    } else {
                      const num = parseInt(val);
                      if (!isNaN(num) && num >= 0 && num <= 50) {
                        setFormData({...formData, passengers: num});
                      }
                    }
                  }}
                  className="w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border border-slate-200 rounded-lg
                    focus:border-navy-500 focus:ring-2 focus:ring-navy-200
                    transition-all outline-none"
                  data-testid="passengers-input"
                />
                <p className="text-xs text-slate-500 mt-1">{t('hero.passengers_hint')}</p>
              </div>
              
              {/* Roundtrip */}
              <div className="flex items-center gap-2 sm:gap-3">
                <input
                  type="checkbox"
                  id="roundtrip"
                  checked={formData.isRoundtrip}
                  onChange={(e) => setFormData({...formData, isRoundtrip: e.target.checked, returnAt: '', returnTime: '12:00'})}
                  className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 border-slate-300 rounded 
                    focus:ring-2 focus:ring-amber-200"
                  data-testid="roundtrip-checkbox"
                />
                <label htmlFor="roundtrip" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-700 cursor-pointer">
                  <Repeat className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('hero.roundtrip')}
                </label>
              </div>
              
              {/* Return Date and Time - Only visible when roundtrip */}
              {formData.isRoundtrip && (
                <div className="grid grid-cols-2 gap-2 sm:gap-3 pl-2 border-l-2 border-amber-400">
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1 sm:gap-2">
                      <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" />
                      <span className="truncate">{t('hero.return_date')}</span>
                    </label>
                    <input
                      type="date"
                      value={formData.returnAt}
                      onChange={(e) => setFormData({...formData, returnAt: e.target.value})}
                      min={formData.pickupAt || toLocalDateString()}
                      max={toLocalDateString(new Date(new Date().setFullYear(new Date().getFullYear() + 4)))}
                      className="w-full h-11 sm:h-12 px-2 sm:px-4 text-sm sm:text-base bg-white border border-slate-200 rounded-lg
                        focus:border-navy-500 focus:ring-2 focus:ring-navy-200
                        transition-all outline-none"
                      data-testid="return-date-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1 sm:gap-2">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" />
                      <span className="truncate">{t('hero.return_time')}</span>
                    </label>
                    <input
                      type="time"
                      value={formData.returnTime}
                      onChange={(e) => setFormData({...formData, returnTime: e.target.value})}
                      className="w-full h-11 sm:h-12 px-2 sm:px-4 text-sm sm:text-base bg-white border border-slate-200 rounded-lg 
                        focus:border-navy-500 focus:ring-2 focus:ring-navy-200 
                        transition-all outline-none"
                      data-testid="return-time-input"
                    />
                  </div>
                </div>
              )}
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 sm:h-12 bg-navy-900 hover:bg-navy-800 text-white 
                  text-sm sm:text-base font-semibold rounded-lg shadow-lg shadow-navy-900/30 
                  transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2
                  disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-quote-btn"
              >
                <span className="truncate">{loading ? t('hero.submit_loading') : t('hero.submit')}</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
